import { Injectable } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { AppInsightsService } from 'src/lib/tcs-app-insights/app-insights-initializer.service';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { NewRelicService } from 'src/lib/tcs-new-relic/new-relic-initializer.service';
import { AppState } from 'src/store/appstate';
import { reviveUnsafe } from 'src/store/generated/record-reviver.generated';
import { selectMutationByOperationId } from 'src/store/mutations/mutation-selectors';
import * as PubnubStoreActions from 'src/store/pubnub/pubnub-store.actions';
import { StoreService } from 'src/store/store.service';
import {
  EntitiesReceived,
  EntitySnapshot,
  EntitySnapshotCollection,
  EntitySnapshotDto,
} from 'src/util/entity-store/entity-store.actions';
import * as MutationActions from 'src/util/graphql/mutation-actions';
import { MutationEffect, MutationSuccess } from 'src/util/graphql/mutation-actions';
import { filterNull } from 'src/util/rxjs/operators/filter-null';

export function parseOperationId(activityId: string) {
  const pipeIndex = activityId.indexOf('|');

  const withoutPrefix = pipeIndex > 0 ? activityId.substring(pipeIndex + 1) : activityId;

  const dotIndex = withoutPrefix.indexOf('.');
  const operationId = dotIndex > 0 ? withoutPrefix.substring(0, dotIndex) : withoutPrefix;

  return operationId;
}

const effectTimeoutDuration = 5000;

@Injectable({ providedIn: 'root' })
export class PubnubEntityStoreEffects {
  public corruptEffects$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<MutationActions.MutationRequest>(MutationActions.MUTATION_REQUEST),
        switchMap((request) =>
          this._store.select(selectMutationByOperationId(request.operationId)),
        ),
        filter((mutation) => mutation != null && mutation.isError && mutation.hasEffect),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        map((m) => m!),
        tap((mutation) => {
          if (this._appInsightsService.appInsights != null) {
            this._appInsightsService.appInsights?.trackException({
              exception: new Error('CorruptMutationEffect'),
              properties: {
                operationId: mutation.operationId,
              },
              severityLevel: SeverityLevel.Error,
            });
          }

          this._newRelicService.newRelic?.noticeError(new Error('CorruptMutationEffect'), {
            operationId: mutation.operationId,
            severityLevel: SeverityLevel.Error,
          });
        }),
      ),
    { dispatch: false },
  );

  public mutationDurations$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<MutationActions.MutationRequest>(MutationActions.MUTATION_REQUEST),
        switchMap((request) =>
          this._store.select(selectMutationByOperationId(request.operationId)),
        ),
        filterNull(),
        filter((mutation) => mutation.hasEffect),
        distinctUntilChanged((a, b) => a.operationId === b.operationId),
        tap((mutation) => {
          if (
            mutation != null &&
            mutation.effectDuration != null &&
            this._appInsightsService.appInsights != null
          ) {
            this._appInsightsService.appInsights.trackEvent({
              name: 'MutationEffectReceived',
              properties: createSafeProps({
                operationId: mutation.operationId,
                effectDuration: mutation.effectDuration,
              }),
            });
          }
          if (
            mutation != null &&
            mutation.effectDuration != null &&
            this._newRelicService.newRelic != null
          ) {
            this._newRelicService.newRelic?.addPageAction('MutationEffectReceived', {
              operationId: mutation.operationId,
              effectDuration: mutation.effectDuration,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  public mutationEffects$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<PubnubStoreActions.MessageReceived>(PubnubStoreActions.MESSAGE_RECEIVED),
      filter((a) => {
        const isEntitySnapsnot = a.message.message.type.includes('EntitySnapshot') === true;
        const activityId = a.message.message.head.activityId;
        return isEntitySnapsnot && activityId != null;
      }),
      map((a) => {
        const activityId = a.message.message.head.activityId as string;
        const operationId = parseOperationId(activityId);
        return new MutationEffect(operationId);
      }),
    ),
  );

  public mutationSuccessEntities$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<MutationSuccess>(MutationActions.MUTATION_SUCCESS),
      map((success) => {
        const logPrefix = 'PubnubEntityStoreEffects.mutationSuccessEntities$';
        const entityMap = success.result.entities;
        const snapshots: EntitySnapshot[] = [];
        if (entityMap != null) {
          Object.entries(entityMap).forEach(([type, entities]) => {
            entities.forEach((state) => {
              try {
                const [record, errors] = reviveUnsafe({ type, state });
                if (record == null) {
                  throw new Error(`${logPrefix}: unable to revive record of type '${type}'`);
                }

                // Entities that are deleted don't return full state, so we expect some errors creating the record.
                // The record will be in an invalid state, but it's deleted so it's purged from the store
                // If it's not deleted, we should log these errors
                if (record.dateDeleted == null && errors.length > 0) {
                  errors.forEach((e) => {
                    this._log.error(
                      `${logPrefix}: error reviving ${type} entity record: ${e}`,
                      createSafeProps({
                        state,
                        mutation: success,
                        operationId: success.operationId,
                      }),
                    );
                  });
                }

                snapshots.push({ key: record.id, source: 'Fetched', state: record, type });
              } catch (err: any) {
                this._log.error(
                  `${logPrefix}: Error serializing data to ${type}Record: ${err.message} (id: ${state.id})`,
                  createSafeProps({
                    state,
                    mutation: success,
                    operationId: success.operationId,
                  }),
                );
              }
            });
          });
          if (snapshots.length > 0) {
            return new EntitiesReceived(snapshots, success.operationId);
          }
        }
        return undefined;
      }),
      filterNull(),
    ),
  );

  public statusUpdates$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<PubnubStoreActions.StatusChange>(PubnubStoreActions.STATUS_CHANGE),
        tap((a) => {
          this._appInsightsService.appInsights?.trackEvent({
            name: 'PubNub:StatusChange',
            properties: createSafeProps({
              ...a.status,
              category: a.status.category,
            }),
          });
          this._newRelicService.newRelic?.addPageAction('PubNub:StatusChange', {
            ...a.status,
            category: a.status.category,
          });
        }),
      ),
    { dispatch: false },
  );

  public entitySnapshots$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<PubnubStoreActions.MessageReceived>(PubnubStoreActions.MESSAGE_RECEIVED),
        filter((a) => a.message.message.type === 'EntitySnapshot'),
        map((a) => a.getMessageBody<EntitySnapshotDto>()),
        tap((snapshot: EntitySnapshotDto) => {
          try {
            this._storeService.upsertFromPubNub([snapshot]);
          } catch (err: any) {
            this._log.error(`Error upserting EntitySnapshot: ${err.mssage}`);
          }
        }),
      ),
    { dispatch: false },
  );

  public entitySnapshotsCollections$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<PubnubStoreActions.MessageReceived>(PubnubStoreActions.MESSAGE_RECEIVED),
        filter((a) => a.message.message.type === 'EntitySnapshotCollection'),
        tap((a) => {
          try {
            const collection = a.getMessageBody<EntitySnapshotCollection>();
            const activityId = a.message.message.head.activityId as string;
            const operationId = parseOperationId(activityId);
            this._storeService.upsertFromPubNub(collection.entities);
          } catch (err) {}
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _actions$: Actions,
    private _store: Store<AppState>,
    private _storeService: StoreService,
    private readonly _log: Logger,
    private _appInsightsService: AppInsightsService,
    private _newRelicService: NewRelicService,
  ) {}
}
